import { useMemo } from 'react'
import {
  formatAltitudeTopFt,
  formatLighted,
  formatObstacleCoordinates,
  formatObstacleHeight
} from '@modules/MapScreen/components/MapObstacles/obstacleFormatters'
import { Assets } from '@nexds/web'
import { useTheme } from 'styled-components'

import {
  CloseButton,
  Header,
  ObstacleBody,
  ObstacleCoordinatesRow,
  ObstacleCopyButton,
  ObstacleField,
  ObstacleFieldLabel,
  ObstacleFieldValuePrimary,
  ObstacleFieldValueSecondary,
  ObstacleFieldValueText,
  ObstacleFieldValueUnit,
  Title
} from './InfoPanel.styles'

import { useI18n } from '@/presentation/hooks/useI18n'
import { ObstacleFeatureInfoData } from '@/presentation/modules/MapScreen/states/FeatureInfoState'
import { GlobalToastUtil } from '@/utils/globalToastUtil'

interface ObstacleFeatureInfoProps {
  title: string
  obstacle: ObstacleFeatureInfoData
  onClose?: () => void
}

function ObstacleFeatureInfo({ title, obstacle, onClose }: ObstacleFeatureInfoProps) {
  const theme = useTheme()
  const { t } = useI18n()

  const unknownLabel = t('MAP_OBSTACLE_INFO_UNKNOWN_VALUE')
  const altitudeTopLabel = useMemo(
    () => formatAltitudeTopFt(obstacle.elevationTopFt, unknownLabel),
    [obstacle.elevationTopFt, unknownLabel]
  )
  const heightLabel = useMemo(
    () => formatObstacleHeight(obstacle.elevationTopFt, obstacle.elevationBaseFt, unknownLabel),
    [obstacle.elevationTopFt, obstacle.elevationBaseFt, unknownLabel]
  )
  const lightedLabel = useMemo(
    () =>
      formatLighted(
        obstacle.lighted,
        t('MAP_OBSTACLE_INFO_LIGHTED_YES'),
        t('MAP_OBSTACLE_INFO_LIGHTED_NO'),
        unknownLabel
      ),
    [obstacle.lighted, t, unknownLabel]
  )
  const coordinatesLabel = useMemo(() => formatObstacleCoordinates(obstacle.coordinates), [obstacle.coordinates])

  const heightParts = useMemo(() => {
    if (heightLabel === unknownLabel) return null

    const match = heightLabel.match(/^(.+?)\s+(\(.+\))$/)
    if (!match) return null

    return { primary: match[1], secondary: match[2] }
  }, [heightLabel, unknownLabel])

  const handleCopyCoordinates = async () => {
    try {
      await navigator.clipboard.writeText(coordinatesLabel)
      GlobalToastUtil.send({
        label: t('TOAST_COORDINATES_COPY_SUCCESS'),
        variant: 'primary'
      })
    } catch {
      GlobalToastUtil.send({
        label: t('TOAST_COORDINATES_COPY_ERROR'),
        variant: 'error'
      })
    }
  }

  return (
    <>
      <Header>
        <Title>{title}</Title>
        <CloseButton onClick={onClose}>
          <Assets.Close size={'md'} color={theme.colors.secondaryL3} />
        </CloseButton>
      </Header>
      <ObstacleBody>
        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_OBSTACLE_INFO_ALTITUDE-TOP_LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValuePrimary>{altitudeTopLabel}</ObstacleFieldValuePrimary>
        </ObstacleField>

        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_OBSTACLE_INFO_HEIGHT_LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValueSecondary>
            {heightParts ? (
              <>
                {heightParts.primary} <ObstacleFieldValueUnit>{heightParts.secondary}</ObstacleFieldValueUnit>
              </>
            ) : (
              heightLabel
            )}
          </ObstacleFieldValueSecondary>
        </ObstacleField>

        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_OBSTACLE_INFO_LIGHTED_LABEL')}</ObstacleFieldLabel>
          <ObstacleFieldValueText>{lightedLabel}</ObstacleFieldValueText>
        </ObstacleField>

        <ObstacleField>
          <ObstacleFieldLabel>{t('MAP_OBSTACLE_INFO_COORDINATES_LABEL')}</ObstacleFieldLabel>
          <ObstacleCoordinatesRow>
            <ObstacleFieldValueText>{coordinatesLabel}</ObstacleFieldValueText>
            <ObstacleCopyButton onClick={handleCopyCoordinates} aria-label={t('MAP_OBSTACLE_INFO_COORDINATES_LABEL')}>
              <Assets.Copy size={'sm'} color={theme.colors.neutralL5} />
            </ObstacleCopyButton>
          </ObstacleCoordinatesRow>
        </ObstacleField>
      </ObstacleBody>
    </>
  )
}

export { ObstacleFeatureInfo }
